import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiClock, FiRefreshCw, FiShield, FiUser } from "react-icons/fi";
import axios from "../../api/axiosInstance";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import { approvalSectionLabels, unifiedApprovalsText } from "./unifiedApprovalsMessages";

const API = "/api/purchasing/approvals/history";
const decisionLabels = { approved: "Approved", rejected: "Rejected", returned: "Returned for correction" };
const dateOf = (value, language) => value && Number.isFinite(new Date(value).getTime())
  ? new Date(value).toLocaleString(language === "es" ? "es-ES" : language === "ca" ? "ca-ES" : "en-GB", { dateStyle: "short", timeStyle: "short" }) : "—";

export default function UnifiedApprovalsHistory({ businessUnitId, refreshKey }) {
  const { languageId } = usePmsTheme();
  const t = unifiedApprovalsText(languageId);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failure, setFailure] = useState(null);
  const version = useRef(0);
  const busy = useRef(false);
  const load = useCallback(async cursor => {
    if (cursor && busy.current) return;
    const current = ++version.current;
    busy.current = true;
    setFailure(null);
    setLoading(!cursor); setLoadingMore(Boolean(cursor));
    if (!cursor) setData(null);
    try {
      const { data: page } = await axios.get(API, { params: { business_unit_id: businessUnitId, limit: 25, ...(cursor ? { cursor } : {}) } });
      if (current !== version.current) return;
      if (Number(page?.business_unit_id) !== businessUnitId || !Array.isArray(page?.items) || typeof page.can_access !== "boolean" || (page.has_more && !page.next_cursor)) throw new Error("Invalid approvals history");
      setData(previous => ({
        ...page,
        items: page.can_access ? [...new Map([...(cursor ? previous?.items || [] : []), ...page.items].map(row => [row.id, row])).values()] : [],
      }));
    } catch (error) {
      if (current === version.current) {
        if (error.response?.status === 403) setData(null);
        setFailure({ error, cursor });
      }
    } finally {
      if (current === version.current) { busy.current = false; setLoading(false); setLoadingMore(false); }
    }
  }, [businessUnitId]);
  useEffect(() => {
    load();
    return () => { version.current += 1; busy.current = false; };
  }, [load, refreshKey]);

  if (loading) return <div className="unifiedApprovals__loading" role="status"><span>{t("Loading…")}</span><div aria-hidden="true" className="unifiedApprovals__skeleton" /><div aria-hidden="true" className="unifiedApprovals__skeleton" /></div>;
  if (data?.can_access === false || failure?.error.response?.status === 403) return <div className="unifiedApprovals__state" role="alert"><FiShield aria-hidden="true" /><p>{t("You do not have approval access in this business unit.")}</p></div>;
  return <>
    <div className="unifiedApprovals__toolbar">
      <p className="unifiedApprovals__historyScope">{t("Decisions on your requests and decisions you recorded in this business unit.")}</p>
      <span className="unifiedApprovals__sort"><FiClock aria-hidden="true" />{t("Newest first")}</span>
    </div>
    {data?.items.length > 0 && <ol className="unifiedApprovals__list" aria-label={t("Approval history")}>{data.items.map(row => <li key={row.id} className="unifiedApprovals__historyRow">
      <div className="unifiedApprovals__document">
        <span className="unifiedApprovals__historyType">{t(approvalSectionLabels[row.type] || "Request")}</span>
        {row.is_requester && <span className="unifiedApprovals__ownRequest">{t("My request")}</span>}
        <h3>{row.reference || `${t("Request")} #${row.document_id}`}</h3>
        {row.description && <p>{row.description}</p>}
      </div>
      <span className={`unifiedApprovals__decision unifiedApprovals__decision--${row.decision}`}>{t(decisionLabels[row.decision] || "Decision")}</span>
      <dl className="unifiedApprovals__historyMeta">
        <div><dt><FiClock aria-hidden="true" />{t("Decision date")}</dt><dd>{dateOf(row.decided_at, languageId)}</dd></div>
        <div><dt><FiUser aria-hidden="true" />{t("Decided by")}</dt><dd>{row.actor_name || "—"}</dd></div>
        <div><dt>{t("Requested by")}</dt><dd>{row.requester_name || "—"}</dd></div>
      </dl>
      {row.comments && <p className="unifiedApprovals__historyComment"><strong>{t("Comment")}</strong><span>{row.comments}</span></p>}
    </li>)}</ol>}
    {!failure && data && !data.items.length && <div className="unifiedApprovals__state unifiedApprovals__empty" role="status"><span className="unifiedApprovals__emptyIcon" aria-hidden="true"><FiClock /></span><strong>{t("No history yet")}</strong><p>{t("There are no recorded decisions on your requests or by you in this business unit.")}</p></div>}
    {failure && <div className="unifiedApprovals__state unifiedApprovals__state--error"><FiClock aria-hidden="true" /><p role="alert">{t("Approval history could not be loaded.")}</p><button type="button" className="unifiedApprovals__button" onClick={() => load(failure.cursor)}><FiRefreshCw aria-hidden="true" />{t("Retry")}</button></div>}
    {!failure && data?.has_more && <div className="unifiedApprovals__historyPagination"><button type="button" className="unifiedApprovals__button" disabled={loadingMore} onClick={() => load(data.next_cursor)}>{t(loadingMore ? "Loading…" : "Load older decisions")}</button>{loadingMore && <span className="unifiedApprovals__srOnly" role="status">{t("Loading…")}</span>}</div>}
  </>;
}
